import React, { useEffect, useState } from 'react';
import { Offcanvas, Tab, Nav, Form } from 'react-bootstrap';
import { useLang } from "../../contexts/LanguageContext";
import fetchWithAuth from '../../api/fetchWithAuth';
import apiUrls from '../../api/apiUrls';
import './ChapterSettingsOffcanvas.css';

// Importar imágenes
import dialect_img from '../../images/dialect.svg';
import audio_speed from '../../images/audio-speed.svg';
import autoplay from '../../images/autoplay.svg';
import dictionary from '../../images/dictionary.svg';
import ipa from '../../images/ipa.svg';
import fontsize from '../../images/fontsize.svg';
import { AmbientSelector } from '../player/AmbientSelector';

const dialectOptions = {
  'es_419': 'Español (latinoamérica)',
  'es_ES': 'Español'
};

const availableDicts = {
  'esp': 'Español',
  'eng': 'English',
  'fra': 'Français'
};

function createSelectWithArray(options, name, onChange, value) {
  const size = Math.min(Math.max(Object.keys(options).length, 2), 5);
  return (
    <select name={name} className="settings-form-select" size={size} id={name} onChange={onChange} value={value}>
      {Object.entries(options).map(([value, label]) => (
        <option className="settings-menu-item" value={value} key={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

const updatePreferences = async (newPreferences) => {
  //console.log("Actualizando preferencias", newPreferences);
  try {
    const response = await fetchWithAuth(apiUrls.apiUrlUpdatePreferences, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newPreferences),
    });
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Error desconocido');
    } 
    
  } catch (err) {
    console.error('Error al actualizar las preferencias', err);
  }
};

function SettingsOffcanvas({ show, handleClose, onDialectChange, onFontSizeChange, onShowLemmaChange, onDictLanguageChange, onSpeedChange, onAutoPlayChange, preferences, bookLanguage }) {
  /* Inciar constante de traducción */
  const { t } = useLang();

  const [localPreferences, setLocalPreferences] = useState({
    dialectCode: '',
    textSpeed: 1,
    autoPlay: false,
    dictLanguage: 'esp',
    showLemma: false,
    fontSize: 2,
    ambientTrackId: null,
    ambientVolume: 0.5
  });

  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        dialectCode: preferences.dialect_code || '',
        textSpeed: preferences.text_speed !== undefined ? preferences.text_speed : 2,
        autoPlay: preferences.auto_play === 1,
        dictLanguage: preferences.dict_language || 'esp',
        showLemma: preferences.show_IPA === 1,
        fontSize: preferences.font_size !== undefined ? preferences.font_size : 2,
        ambientTrackId: preferences.ambient_track_id || null,
        ambientVolume: preferences.ambient_volume !== undefined ? parseFloat(preferences.ambient_volume) : 0.5
      });
    }
  }, [preferences]);

  useEffect(() => {
  }, [localPreferences.dialectCode]);

  const handlePreferenceChange = async (key, value) => {
    const updatedPreferences = { ...localPreferences, [key]: value };
    setLocalPreferences(updatedPreferences);
    
    // Adjuntamos bookLanguage para que el backend sepa a qué tabla de idiomas asociar el cambio
    await updatePreferences({ ...updatedPreferences, bookLanguage });
  };

  const handleDialectChange = async (event) => {
    const newDialect = event.target.value;
    await handlePreferenceChange('dialectCode', newDialect);
    onDialectChange(newDialect); // Llamar a la función pasada como prop para actualizar el audio
  };

  const handleSpeedChange = (event) => {
    const newSpeed = event.target.value;
    handlePreferenceChange('textSpeed', newSpeed);
    onSpeedChange(newSpeed); // Call the prop function to update audio speed
  };

  const handleAutoPlayChange = (event) => {
    const newAutoPlay = event.target.checked ? 1 : 0;
    handlePreferenceChange('autoPlay', newAutoPlay);
    onAutoPlayChange(newAutoPlay === 1); // Notify parent about the change
  };

  const handleShowLemmaChange = (event) => {
    const newShowLemma = event.target.checked ? 1 : 0;
    handlePreferenceChange('showLemma', newShowLemma);
    onShowLemmaChange(newShowLemma); // Llamar a la función pasada como prop para actualizar la visibilidad de la fonología
  };

  const handleDictLanguageChange = (event) => {
    const newDictLanguage = event.target.value;
    handlePreferenceChange('dictLanguage', newDictLanguage);
    onDictLanguageChange(newDictLanguage); // Llamar a la función pasada como prop para actualizar el diccionario
  };

  const handleFontSizeChange = (event) => {
    const newFontSize = event.target.value;
    handlePreferenceChange('fontSize', newFontSize);
    onFontSizeChange(newFontSize); // Llamar a la función pasada como prop para actualizar el tamaño del texto
  };

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end">
      <Offcanvas.Header closeButton closeVariant="white">  
        <Offcanvas.Title>{t("CHAPTER_SETTINGS_TITLE")}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="offcanvas-body">
        <Tab.Container defaultActiveKey="tab1">
          <form id="settings">
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="tab1">{t("CHAPTER_SETTINGS_AUDIO")}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tab2">{t("CHAPTER_SETTINGS_DICTIONARY")}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tab3">{t("CHAPTER_SETTINGS_TEXT")}</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content className='settings-form'>
              <Tab.Pane eventKey="tab1">
                <div className="pb-5">
                  <label htmlFor="dialectCode" className="settings-label"><img src={dialect_img} className="settingsLabelIcon" alt={t("CHAPTER_DIALECT_ICON")} />{t("CHAPTER_DIALECT")}</label>
                  
                  {createSelectWithArray(dialectOptions, "dialectCode", handleDialectChange, localPreferences.dialectCode)}
                
                </div>
                <div className="pb-5">
                  <label htmlFor="speedSelect" className="settings-label"><img src={audio_speed} className="settingsLabelIcon" alt={t("CHAPTER_SPEED_ICON")} />{t("CHAPTER_SPEED")}</label>
                  <input name="textSpeed" type="range" className="form-range" min="0" max="4" list="tickmarks" id="speedSelect" onChange={handleSpeedChange} value={localPreferences.textSpeed} />
                  <datalist id="tickmarks">
                    <option value="0" label="0.5x"></option>
                    <option value="1" label="0.75x"></option>
                    <option value="2" label="1x"></option>
                    <option value="3" label="1.25x"></option>
                    <option value="4" label="1.5x"></option>
                  </datalist>
                </div>
                <div className="pb-5 container-fluid">
                  <div className="row">
                    <div className="col-10 p-0">
                      <label htmlFor="autoPlay" className="settings-label"><img src={autoplay} className="settingsLabelIcon" alt={t("CHAPTER_AUTO_PLAY_ICON")}/>{t("CHAPTER_AUTO_PLAY")}</label>
                      <p className="settingsDesc">{t("CHAPTER_AUTO_PLAY_DESC")}</p>
                    </div>
                    <div className="col-2 form-check form-switch">
                      <Form.Check 
                        type="switch"
                        id="autoPlay"
                        name="autoPlay"
                        onChange={handleAutoPlayChange}
                        checked={localPreferences.autoPlay}
                      />
                    </div>
                  </div>
                </div>
                <hr className="settings-divider" />
                <AmbientSelector 
                  preferences={preferences} 
                  onChange={handlePreferenceChange} 
                />
              </Tab.Pane>

              <Tab.Pane eventKey="tab2">
                <div className="pb-5">
                  <label htmlFor="dictLanguage" className="settings-label"><img src={dictionary} className="settingsLabelIcon" alt={t("CHAPTER_DICTIONARY_LAGUAGE_ICON")} />{t("CHAPTER_DICTIONARY_LAGUAGE")}</label>

                  {createSelectWithArray(availableDicts, "dictLanguage", handleDictLanguageChange, localPreferences.dictLanguage)}

                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="tab3">
                <div className="pb-5 container-fluid">
                  <div className="row">
                    <div className="col-10 p-0">
                      <label htmlFor="showLemma" className="settings-label"><img src={ipa} className="settingsLabelIcon" alt={t("CHAPTER_SHOW_PHONOLOGY_ICON")} />{t("CHAPTER_SHOW_PHONOLOGY")}</label>
                      <p className="settingsDesc">{t("CHAPTER_SHOW_PHONOLOGY_DESC")}</p>
                    </div>
                    <div className="col-2 form-check form-switch">
                      <Form.Check 
                        type="switch"
                        id="showLemma"
                        name="showLemma"
                        onChange={handleShowLemmaChange}
                        checked={localPreferences.showLemma}
                      />
                    </div>
                  </div>
                </div>

                <div className="container-fluid">
                  <div className="row">
                    <div className="col-12 p-0">
                      <label htmlFor="fontSizeDesc" className="settings-label"><img src={fontsize} className="settingsLabelIcon" alt={t("CHAPTER_TEXT_SIZE_ICON")} />{t("CHAPTER_TEXT_SIZE")}</label>
                    </div>
                    <div className="col-12 p-0 text-center">
                      <div className={`uTextButton ${parseInt(localPreferences.fontSize, 10)  === 0 ? 'uTextSelected' : ''}`}>
                        <input type="radio" name="fontSize" value="0" id="uTextMenuA" onChange={handleFontSizeChange}  />
                        <label htmlFor="uTextMenuA" className="uTextA" id="labelFontSizeA">{t("CHAPTER_TS_CHARACTER")}</label>
                      </div>
                      <div className={`uTextButton ${parseInt(localPreferences.fontSize, 10)  === 1 ? 'uTextSelected' : ''}`}>
                        <input type="radio" name="fontSize" value="1" id="uTextMenuB" onChange={handleFontSizeChange}  />
                        <label htmlFor="uTextMenuB" className="uTextB" id="labelFontSizeB">{t("CHAPTER_TS_CHARACTER")}</label>
                      </div>
                      <div className={`uTextButton ${parseInt(localPreferences.fontSize, 10)  === 2 ? 'uTextSelected' : ''}`}>
                        <input type="radio" name="fontSize" value="2" id="uTextMenuC" onChange={handleFontSizeChange} />
                        <label htmlFor="uTextMenuC" className="uTextC" id="labelFontSizeC">{t("CHAPTER_TS_CHARACTER")}</label>
                      </div>
                      <div className={`uTextButton ${parseInt(localPreferences.fontSize, 10) === 3 ? 'uTextSelected' : ''}`}>
                        <input type="radio" name="fontSize" value="3" id="uTextMenuD" onChange={handleFontSizeChange} />
                        <label htmlFor="uTextMenuD" className="uTextD" id="labelFontSizeD">{t("CHAPTER_TS_CHARACTER")}</label>
                      </div>
                      <div className={`uTextButton ${parseInt(localPreferences.fontSize, 10) === 4 ? 'uTextSelected' : ''}`}>
                        <input type="radio" name="fontSize" value="4" id="uTextMenuE" onChange={handleFontSizeChange} />
                        <label htmlFor="uTextMenuE" className="uTextE" id="labelFontSizeE">{t("CHAPTER_TS_CHARACTER")}</label>
                      </div>
                    </div> 
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </form>
        </Tab.Container>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default SettingsOffcanvas;